import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditHrEmployeeSelectField } from '../../../EditHrEmployeeSelectField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const QA_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'indiaQaCheckNeeded', formField: 'indiaQaCheckNeeded' },
  { apiField: 'indiaQaCheckedBy', formField: 'indiaQaCheckedById' },
  { apiField: 'lastIndiaQaCheckedBy', formField: 'lastIndiaQaCheckedById' },
  {
    apiField: 'indiaQaDate',
    formField: 'lastIndiaQaDate',
    isDate: true
  },
  { apiField: 'india2QaCheckNeeded', formField: 'india2QaCheckNeeded' },
  { apiField: 'india2QaCheckedBy', formField: 'india2QaCheckedById' },
  {
    apiField: 'lastIndia2QaCheckedBy',
    formField: 'lastIndia2QaCheckedById'
  },
  {
    apiField: 'india2QaDate',
    formField: 'lastIndia2QaDate',
    isDate: true
  },
  { apiField: 'ny2QaCheckNeeded', formField: 'ny2QaCheckNeeded' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        indiaQaCheckNeeded
        indiaQaCheckedBy {
          id
          name
        }
        lastIndiaQaCheckedBy {
          id
          name
        }
        indiaQaDate
        india2QaCheckNeeded
        india2QaCheckedBy {
          id
          name
        }
        lastIndia2QaCheckedBy {
          id
          name
        }
        india2QaDate
        ny2QaCheckNeeded
      }
    }
  }
`;

export interface EditQaInfoDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditQaInfoDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditQaInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(QA_INFO_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save QA info');
      } else {
        toast.success('QA info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving QA info:', error);
      toast.error('Failed to save QA info');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit QA Info"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.indiaQaCheckNeeded}
          label="India - QA Check Needed"
          onCheckedChange={indiaQaCheckNeeded => {
            updateField('indiaQaCheckNeeded', indiaQaCheckNeeded);
          }}
        />
        <EditHrEmployeeSelectField
          label="India QA Checked By"
          onChange={(id, name) => {
            onChange({
              ...value,
              indiaQaCheckedBy: name,
              indiaQaCheckedById: id
            });
          }}
          value={
            value.indiaQaCheckedById
              ? {
                  id: value.indiaQaCheckedById,
                  name: value.indiaQaCheckedBy
                }
              : null
          }
        />
        <EditHrEmployeeSelectField
          label="Last India QA Checked By"
          onChange={(id, name) => {
            onChange({
              ...value,
              lastIndiaQaCheckedBy: name,
              lastIndiaQaCheckedById: id
            });
          }}
          value={
            value.lastIndiaQaCheckedById
              ? {
                  id: value.lastIndiaQaCheckedById,
                  name: value.lastIndiaQaCheckedBy
                }
              : null
          }
        />
        <EditFormField
          label="Last India QA Date"
          onChange={lastIndiaQaDate => {
            updateField('lastIndiaQaDate', lastIndiaQaDate);
          }}
          type="date"
          value={toDateInputValue(value.lastIndiaQaDate)}
        />
        <EditCheckboxField
          checked={value.india2QaCheckNeeded}
          label="India2 - QA Check Needed"
          onCheckedChange={india2QaCheckNeeded => {
            updateField('india2QaCheckNeeded', india2QaCheckNeeded);
          }}
        />
        <EditHrEmployeeSelectField
          label="India2 QA Checked By"
          onChange={(id, name) => {
            onChange({
              ...value,
              india2QaCheckedBy: name,
              india2QaCheckedById: id
            });
          }}
          value={
            value.india2QaCheckedById
              ? {
                  id: value.india2QaCheckedById,
                  name: value.india2QaCheckedBy
                }
              : null
          }
        />
        <EditHrEmployeeSelectField
          label="Last India2 QA Checked By"
          onChange={(id, name) => {
            onChange({
              ...value,
              lastIndia2QaCheckedBy: name,
              lastIndia2QaCheckedById: id
            });
          }}
          value={
            value.lastIndia2QaCheckedById
              ? {
                  id: value.lastIndia2QaCheckedById,
                  name: value.lastIndia2QaCheckedBy
                }
              : null
          }
        />
        <EditFormField
          label="Last India2 QA Date"
          onChange={lastIndia2QaDate => {
            updateField('lastIndia2QaDate', lastIndia2QaDate);
          }}
          type="date"
          value={toDateInputValue(value.lastIndia2QaDate)}
        />
        <EditCheckboxField
          checked={value.ny2QaCheckNeeded}
          label="NY2 - QA Check Needed"
          onCheckedChange={ny2QaCheckNeeded => {
            updateField('ny2QaCheckNeeded', ny2QaCheckNeeded);
          }}
        />
      </div>
    </EditDialog>
  );
}
